import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents } from 'react-leaflet';
import { Icon, LatLngExpression } from 'leaflet';
import { Location } from '../types';

// Custom bird icon
const birdIcon = new Icon({
  iconUrl: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#3B82F6" stroke="#ffffff" stroke-width="2"/>
      <circle cx="12" cy="12" r="6" fill="#1E40AF"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// Origin icon
const originIcon = new Icon({
  iconUrl: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="10" r="8" fill="#22C55E" stroke="#ffffff" stroke-width="2"/>
      <circle cx="12" cy="10" r="4" fill="#15803D"/>
      <path d="M12 18 L8 22 L16 22 Z" fill="#22C55E"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

// Destination icon
const destinationIcon = new Icon({
  iconUrl: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#EF4444" stroke="#ffffff" stroke-width="2"/>
      <circle cx="12" cy="12" r="6" fill="#DC2626"/>
      <circle cx="12" cy="12" r="2" fill="#ffffff"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

// Map click handler component
interface MapClickHandlerProps {
  onClick: (e: any) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onClick }) => {
  useMapEvents({
    click: onClick,
  });
  return null;
};

interface InteractiveMapProps {
  origin: Location;
  destination: Location;
  currentLocation?: Location;
  className?: string;
  onLocationClick?: (location: Location) => void;
  showFlightPath?: boolean;
  zoom?: number;
  fitBounds?: boolean;
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({
  origin,
  destination,
  currentLocation,
  className = "w-full h-96",
  onLocationClick,
  showFlightPath = true,
  zoom = 6,
  fitBounds = false
}) => {
  // Calculate map center and bounds
  const centerLat = (origin.latitude + destination.latitude) / 2;
  const centerLng = (origin.longitude + destination.longitude) / 2;
  const center: LatLngExpression = [centerLat, centerLng];

  // Calculate optimal zoom level to fit both points
  const calculateOptimalZoom = (): number => {
    if (!fitBounds) return zoom;
    
    const latDiff = Math.abs(origin.latitude - destination.latitude);
    const lngDiff = Math.abs(origin.longitude - destination.longitude);
    const maxDiff = Math.max(latDiff, lngDiff);
    
    // Zoom levels based on coordinate difference
    if (maxDiff > 100) return 2;  // Continental/global view
    if (maxDiff > 50) return 3;   // Multi-country view
    if (maxDiff > 20) return 4;   // Country view
    if (maxDiff > 10) return 5;   // Regional view
    if (maxDiff > 5) return 6;    // State/province view
    if (maxDiff > 2) return 7;    // City-to-city view
    if (maxDiff > 1) return 8;    // Metropolitan view
    if (maxDiff > 0.5) return 9;  // City view
    return 10;                    // Local view
  };

  const optimalZoom = calculateOptimalZoom();

  // Flight path coordinates
  const flightPath: LatLngExpression[] = [
    [origin.latitude, origin.longitude],
    [destination.latitude, destination.longitude]
  ];

  // Add current location to path if available
  const progressPath: LatLngExpression[] = currentLocation
    ? [
        [origin.latitude, origin.longitude],
        [currentLocation.latitude, currentLocation.longitude]
      ]
    : [];

  const handleMapClick = (e: any) => {
    if (onLocationClick) {
      const { lat, lng } = e.latlng;
      onLocationClick({
        latitude: lat,
        longitude: lng,
        address: `${lat.toFixed(4)}, ${lng.toFixed(4)}`
      });
    }
  };

  return (
    <div className={className}>
      <MapContainer
        center={center}
        zoom={optimalZoom}
        className="w-full h-full rounded-lg"
      >
        {/* Map click handler */}
        <MapClickHandler onClick={handleMapClick} />
        
        {/* OpenStreetMap tiles */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Flight path */}
        {showFlightPath && (
          <>
            {/* Planned route (dotted) */}
            <Polyline
              positions={flightPath}
              color="#3B82F6"
              weight={2}
              opacity={0.6}
              dashArray="5, 10"
            />
            
            {/* Progress path (solid) */}
            {progressPath.length > 1 && (
              <Polyline
                positions={progressPath}
                color="#10B981"
                weight={3}
                opacity={0.8}
              />
            )}
          </>
        )}

        {/* Origin marker */}
        <Marker
          position={[origin.latitude, origin.longitude]}
          icon={originIcon}
        >
          <Popup>
            <div className="text-sm">
              <strong>Origin</strong><br />
              {origin.address || `${origin.latitude.toFixed(4)}, ${origin.longitude.toFixed(4)}`}
              {origin.city && <><br />{origin.city}, {origin.country}</>}
            </div>
          </Popup>
        </Marker>

        {/* Destination marker */}
        <Marker
          position={[destination.latitude, destination.longitude]}
          icon={destinationIcon}
        >
          <Popup>
            <div className="text-sm">
              <strong>Destination</strong><br />
              {destination.address || `${destination.latitude.toFixed(4)}, ${destination.longitude.toFixed(4)}`}
              {destination.city && <><br />{destination.city}, {destination.country}</>}
            </div>
          </Popup>
        </Marker>

        {/* Current location (bird) */}
        {currentLocation && (
          <Marker
            position={[currentLocation.latitude, currentLocation.longitude]}
            icon={birdIcon}
          >
          <Popup>
            <div className="text-sm">
              <strong>Bird Location</strong><br />
              {currentLocation.address || `${currentLocation.latitude.toFixed(4)}, ${currentLocation.longitude.toFixed(4)}`}
              <br />
              <em>En route to destination</em>
            </div>
          </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default InteractiveMap;
